import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { CheckIcon } from '@heroicons/react/24/outline';
import { obtenerCristalesPorTipo, agregarCristal, obtenerCristales, obtenerCatalogosCristales } from '../api/backend_v2';
import { calcularPrecioEfectivoConDescuento, cargarDescuentoEfectivoSeguro, DESCUENTO_EFECTIVO_DEFAULT } from '../utils/precios';

const CristalSearchInput = ({
  name,
  value,
  onChange,
  onCristalSelect,
  tipoAnteojo = "Monofocal",
  placeholder = "Buscar cristal...",
  required = false
}) => {
  const [cristalesDisponibles, setCristalesDisponibles] = useState([]);
  const [filteredCristales, setFilteredCristales] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [descuentoEfectivo, setDescuentoEfectivo] = useState(DESCUENTO_EFECTIVO_DEFAULT);
  const [precioEfectivoManual, setPrecioEfectivoManual] = useState(false);
  const [catalogos, setCatalogos] = useState({
    marcas_cristales: [],
    productos_cristales: [],
    materiales_cristales: [],
    laboratorios: [],
    tratamientos: [],
  });
  const [modoNuevoCatalogoCampo, setModoNuevoCatalogoCampo] = useState({
    marca: false,
    producto: false,
    material: false,
    laboratorio: false,
    tratamiento: false,
  });
  const [newCristalForm, setNewCristalForm] = useState({
    tipo_vision: tipoAnteojo,
    marca: '',
    producto: '',
    laboratorio: '',
    material: '',
    tratamiento: '',
    indice: '',
    stock: true,
    antirreflejo: false,
    tiene_seguro: false,
    tiene_promocion: false,
    precio_publico: '',
    precio_efectivo: ''
  });

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const modalAbiertoRef = useRef(false);
  const seleccionandoRef = useRef(false);

  // Cargar cristales disponibles al montar el componente y cuando cambie el tipo
  useEffect(() => {
    cargarCristales();
    cargarCatalogos();
  }, [tipoAnteojo]);

  useEffect(() => {
    cargarDescuentoEfectivoSeguro().then(setDescuentoEfectivo);
  }, []);

  const calcularPrecioEfectivo = (precioPublico) => {
    return calcularPrecioEfectivoConDescuento(precioPublico, descuentoEfectivo);
  };

  const cargarCatalogos = async () => {
    try {
      const response = await obtenerCatalogosCristales(false);
      setCatalogos({
        marcas_cristales: (response?.marcas_cristales || []).filter((i) => i?.activo),
        productos_cristales: (response?.productos_cristales || []).filter((i) => i?.activo),
        materiales_cristales: (response?.materiales_cristales || []).filter((i) => i?.activo),
        laboratorios: (response?.laboratorios || []).filter((i) => i?.activo),
        tratamientos: (response?.tratamientos || []).filter((i) => i?.activo),
      });
    } catch (error) {
      console.error('Error al cargar catálogos de cristales:', error);
      setCatalogos({ marcas_cristales: [], productos_cristales: [], materiales_cristales: [], laboratorios: [], tratamientos: [] });
    }
  };

  const opcionesCatalogo = {
    marcas: catalogos.marcas_cristales.map((item) => item.nombre).filter(Boolean),
    productos: catalogos.productos_cristales.map((item) => item.nombre).filter(Boolean),
    materiales: catalogos.materiales_cristales.map((item) => item.nombre).filter(Boolean),
    laboratorios: catalogos.laboratorios.map((item) => item.nombre).filter(Boolean),
    tratamientos: catalogos.tratamientos.map((item) => item.nombre).filter(Boolean),
  };

  const valorEnCatalogo = (lista, valor) => {
    const target = String(valor || '').trim().toLowerCase();
    if (!target) return true;
    return (lista || []).some((item) => String(item || '').trim().toLowerCase() === target);
  };

  // Actualizar valor del input cuando cambie la prop value
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Actualizar tipo_vision en el formulario cuando cambie tipoAnteojo
  useEffect(() => {
    setNewCristalForm(prev => ({
      ...prev,
      tipo_vision: tipoAnteojo
    }));
  }, [tipoAnteojo]);

  const cargarCristales = async () => {
    setLoading(true);
    try {
      console.log('🔍 Cargando cristales para tipo:', tipoAnteojo);
      
      // Intentar primera opción: obtener por tipo específico
      let response = await obtenerCristalesPorTipo(tipoAnteojo);
      console.log('✅ Respuesta de API (por tipo):', response);
      
      let cristales = response.cristales || response || [];
      console.log('📦 Cristales disponibles por tipo:', cristales);
      console.log('📊 Cantidad de cristales:', cristales.length);
      
      // Si no hay cristales por tipo, intentar traer todos y filtrar
      if (cristales.length === 0) {
        console.log('⚠️ No hay cristales del tipo específico, intentando traer todos...');
        const todosResponse = await obtenerCristales();
        const todosCristales = todosResponse.cristales || todosResponse || [];
        console.log('📋 Total de cristales en BD:', todosCristales.length);
        
        // Filtrar por tipo
        cristales = todosCristales.filter(c => 
          c.tipo_vision === tipoAnteojo || 
          c.tipo_vision?.toLowerCase() === tipoAnteojo?.toLowerCase()
        );
        console.log('🔎 Cristales después de filtrar por tipo:', cristales.length);
      }
      
      setCristalesDisponibles(cristales);
    } catch (error) {
      console.error('❌ Error al cargar cristales:', error);
      console.error('📋 Detalles del error:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      // Intentar una última opción: traer todos los cristales
      try {
        console.log('🔄 Intentando traer todos los cristales como fallback...');
        const allResponse = await obtenerCristales();
        const allCristales = allResponse.cristales || allResponse || [];
        console.log('📋 Cristales disponibles (fallback):', allCristales);
        setCristalesDisponibles(allCristales);
      } catch (fallbackError) {
        console.error('❌ Error en el fallback:', fallbackError);
        setCristalesDisponibles([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Filtrar cristales basado en el input
  useEffect(() => {
    if (inputValue.trim() === '') {
      setFilteredCristales(cristalesDisponibles);
    } else {
      const searchTerm = inputValue.toLowerCase();
      const filtered = cristalesDisponibles.filter(cristal =>
        cristal.tipo_vision.toLowerCase().includes(searchTerm) ||
        cristal.marca.toLowerCase().includes(searchTerm) ||
        (cristal.producto?.toLowerCase().includes(searchTerm)) ||
        (cristal.material?.toLowerCase().includes(searchTerm)) ||
        `${cristal.tipo_vision} ${cristal.marca}`.toLowerCase().includes(searchTerm) ||
        `${cristal.marca} ${cristal.producto || ''}`.toLowerCase().includes(searchTerm)
      );
      setFilteredCristales(filtered);
    }
  }, [inputValue, cristalesDisponibles]);

  // Manejar cambios en el input
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setShowDropdown(true);

    // Notificar al componente padre
    onChange({ target: { name, value: newValue } });
  };

  // Seleccionar cristal del dropdown
  const selectCristal = (cristal) => {
    seleccionandoRef.current = true;
    const partes = [cristal.tipo_vision, '-', cristal.marca];
    if (cristal.producto) partes.push('-', cristal.producto);
    if (cristal.material) partes.push(`(${cristal.material})`);
    const displayValue = partes.join(' ');
    setInputValue(displayValue);
    setShowDropdown(false);
    onChange({ target: { name, value: displayValue } });
    onCristalSelect?.(cristal);
    // resetear en el siguiente tick, despues de que blur haya corrido
    setTimeout(() => { seleccionandoRef.current = false; }, 200);
  };

  // Marcar seleccion antes del blur (mousedown ocurre antes que click/blur)
  const marcarSeleccionEnCurso = () => {
    seleccionandoRef.current = true;
    // Failsafe para que nunca quede trabado en true
    setTimeout(() => {
      seleccionandoRef.current = false;
    }, 500);
  };

  // Manejar blur del input
  const handleBlur = (e) => {
    // Delay para permitir clicks en el dropdown
    setTimeout(() => {
      if (seleccionandoRef.current || modalAbiertoRef.current || Swal.isVisible()) return;
      if (!dropdownRef.current?.contains(document.activeElement)) {
        const inputValueClean = inputValue.trim();

        if (inputValueClean !== '' && !cristalesDisponibles.some(cristal => {
          const partes = [cristal.tipo_vision, '-', cristal.marca];
          if (cristal.producto) partes.push('-', cristal.producto);
          if (cristal.material) partes.push(`(${cristal.material})`);
          const cristalString = partes.join(' ');
          return cristalString.toLowerCase() === inputValueClean.toLowerCase() ||
                 cristal.tipo_vision.toLowerCase() === inputValueClean.toLowerCase() ||
                 cristal.marca.toLowerCase() === inputValueClean.toLowerCase();
        })) {
          // Cristal no encontrado, mostrar opción para agregarlo
          mostrarOpcionAgregarCristal();
        }
        setShowDropdown(false);
      }
    }, 150);
  };

  // Mostrar opción para agregar cristal nuevo
  const mostrarOpcionAgregarCristal = async () => {
    if (seleccionandoRef.current || modalAbiertoRef.current || Swal.isVisible()) return;
    const cleanInputValue = inputValue.trim();

    const result = await Swal.fire({
      title: 'Cristal no encontrado',
      text: `El cristal "${cleanInputValue}" no está en la base de datos. ¿Desea agregarlo?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, agregar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33'
    });

    if (result.isConfirmed) {
      // Intentar parsear el input para prellenar el formulario
      let marcaInicial = '';

      const separador = cleanInputValue.indexOf('-');
      if (separador >= 0) {
        marcaInicial = cleanInputValue.slice(separador + 1).trim() || cleanInputValue;
      } else {
        marcaInicial = cleanInputValue;
      }

      setNewCristalForm(prev => ({
        ...prev,
        marca: marcaInicial,
        tipo_vision: tipoAnteojo
      }));
      setModoNuevoCatalogoCampo((prev) => ({ ...prev, marca: true }));
      modalAbiertoRef.current = true;
      setShowModal(true);
    }
  };

  // Manejar cambios en el formulario del modal
  const handleModalFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;
    setNewCristalForm(prev => {
      const next = {
        ...prev,
        [name]: nextValue,
      };

      if (name === 'precio_publico' && !precioEfectivoManual) {
        next.precio_efectivo = calcularPrecioEfectivo(nextValue);
      }

      return next;
    });

    if (name === 'precio_efectivo') {
      setPrecioEfectivoManual(String(value || '').trim() !== '');
    }
    if (name === 'precio_publico' && String(value || '').trim() === '' && !precioEfectivoManual) {
      setNewCristalForm(prev => ({ ...prev, precio_efectivo: '' }));
    }
  };

  // Guardar nuevo cristal
  const guardarNuevoCristal = async () => {
    try {
      // Validar campos requeridos
      if (!newCristalForm.tipo_vision || !newCristalForm.marca) {
        Swal.fire({
          title: 'Error',
          text: 'Los campos Tipo de visión y Marca son obligatorios',
          icon: 'error'
        });
        return;
      }

      const cristalData = {
        ...newCristalForm,
        indice: newCristalForm.indice ? parseFloat(newCristalForm.indice) : null,
        precio_publico: newCristalForm.precio_publico ? parseFloat(newCristalForm.precio_publico) : 0.0,
        precio_efectivo: newCristalForm.precio_efectivo ? parseFloat(newCristalForm.precio_efectivo) : null,
        stock: newCristalForm.stock === true || newCristalForm.stock === 'true',
        laboratorio: newCristalForm.laboratorio || null,
        tratamiento: newCristalForm.tratamiento || null,
        tiene_seguro: newCristalForm.tiene_seguro === true || newCristalForm.tiene_seguro === 'true',
        tiene_promocion: newCristalForm.tiene_promocion === true || newCristalForm.tiene_promocion === 'true'
      };

      console.log('Enviando datos del cristal:', cristalData);
      await agregarCristal(cristalData);
      console.log('Cristal agregado exitosamente');

      // Recargar cristales y seleccionar el nuevo
      await cargarCristales();
      console.log('Cristales recargados');

      // Mostrar en el input el cristal recién creado
      const partes = [newCristalForm.tipo_vision, '-', newCristalForm.marca];
      if (newCristalForm.producto) partes.push('-', newCristalForm.producto);
      if (newCristalForm.material) partes.push(`(${newCristalForm.material})`);
      const displayValue = partes.join(' ');
      setInputValue(displayValue);
      onChange({ target: { name, value: displayValue } });
      console.log('Input actualizado con:', displayValue);

      modalAbiertoRef.current = false;
      setShowModal(false);
      console.log('Modal cerrado');

      // Resetear formulario
      setNewCristalForm({
        tipo_vision: tipoAnteojo,
        marca: '',
        producto: '',
        laboratorio: '',
        material: '',
        tratamiento: '',
        indice: '',
        stock: true,
        antirreflejo: false,
        tiene_seguro: false,
        tiene_promocion: false,
        precio_publico: '',
        precio_efectivo: ''
      });
      setPrecioEfectivoManual(false);
      setModoNuevoCatalogoCampo({ marca: false, producto: false, material: false, laboratorio: false, tratamiento: false });

      // Mostrar notificación de éxito
      Swal.fire({
        title: '¡Éxito!',
        text: 'Cristal agregado correctamente',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      });

    } catch (error) {
      console.error('Error completo al agregar cristal:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Error al agregar el cristal',
        icon: 'error'
      });
    }
  };

  // Cerrar modal sin guardar
  const cerrarModal = () => {
    modalAbiertoRef.current = false;
    setShowModal(false);
    setNewCristalForm({
      tipo_vision: tipoAnteojo,
      marca: '',
      producto: '',
      laboratorio: '',
      material: '',
      tratamiento: '',
      indice: '',
      stock: true,
      antirreflejo: false,
      tiene_seguro: false,
      tiene_promocion: false,
      precio_publico: '',
      precio_efectivo: ''
    });
    setPrecioEfectivoManual(false);
    setModoNuevoCatalogoCampo({ marca: false, producto: false, material: false, laboratorio: false, tratamiento: false });
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        name={name}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => setShowDropdown(true)}
        onBlur={handleBlur}
        placeholder={placeholder}
        required={required}
        style={{ width: '100%' }}
        autoComplete="off"
      />

      {/* Loading indicator */}
      {loading && (
        <div style={{
          position: 'absolute',
          right: '10px',
          top: '50%',
          transform: 'translateY(-50%)',
          color: '#666'
        }}>
          ⟳
        </div>
      )}

      {/* Dropdown with suggestions */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderTop: 'none',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          {filteredCristales.length > 0 ? (
            filteredCristales.map((cristal) => (
              <div
                key={cristal.id}
                onClick={() => selectCristal(cristal)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #eee'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
              >
                <div style={{ fontWeight: '500' }}>
                  {cristal.tipo_vision} - {cristal.marca}
                  {cristal.producto && <span style={{ color: '#444', marginLeft: '4px' }}>| {cristal.producto}</span>}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {cristal.material && <span>Material: {cristal.material} | </span>}
                  Stock: {cristal.stock ? 'Stock' : 'Laboratorio'}
                  {cristal.tratamiento ? ` | ${cristal.tratamiento}` : cristal.antirreflejo ? ' | Antirreflejo' : ''}
                  {cristal.precio_publico > 0 && ` | $${Math.round(cristal.precio_publico).toLocaleString('es-AR')}`}
                </div>
              </div>
            ))
          ) : cristalesDisponibles.length === 0 ? (
            <div style={{
              padding: '12px',
              color: '#ff6b6b',
              textAlign: 'center',
              fontSize: '14px'
            }}>
              No hay cristales disponibles para {tipoAnteojo}
            </div>
          ) : (
            <div style={{
              padding: '12px',
              color: '#999',
              textAlign: 'center',
              fontSize: '14px'
            }}>
              No hay coincidencias
            </div>
          )}
        </div>
      )}

      {/* Modal para agregar nuevo cristal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          zIndex: 30000,
          paddingTop: '88px',
          paddingBottom: '24px'
        }} onClick={(e) => {
          if (e.target === e.currentTarget) {
            cerrarModal();
          }
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>
                Agregar Nuevo Cristal
              </h2>
              <button
                onClick={cerrarModal}
                style={{
                  border: 'none',
                  background: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}>
              {/* Fila 1: Tipo de Visión y Marca */}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Tipo de Visión *:
                </label>
                <input
                  type="text"
                  name="tipo_vision"
                  value={newCristalForm.tipo_vision}
                  onChange={handleModalFormChange}
                  required
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontWeight: '500'
                  }}
                  placeholder="Ej: Monofocal, Bifocal, Progressive"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Marca *:
                </label>
                {!modoNuevoCatalogoCampo.marca ? (
                  <select
                    name="marca"
                    value={newCristalForm.marca}
                    onChange={(e) => {
                      if (e.target.value === '__nuevo__') {
                        setModoNuevoCatalogoCampo((prev) => ({ ...prev, marca: true }));
                        setNewCristalForm((prev) => ({ ...prev, marca: '' }));
                        return;
                      }
                      handleModalFormChange(e);
                    }}
                    required
                    style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                  >
                    <option value="">Seleccionar marca...</option>
                    {newCristalForm.marca && !valorEnCatalogo(opcionesCatalogo.marcas, newCristalForm.marca) && (
                      <option value={newCristalForm.marca}>{`Valor actual: ${newCristalForm.marca}`}</option>
                    )}
                    {opcionesCatalogo.marcas.map((item) => <option key={`marca-${item}`} value={item}>{item}</option>)}
                    <option value="__nuevo__">+ Crear nueva marca...</option>
                  </select>
                ) : (
                  <div>
                    <input
                      type="text"
                      name="marca"
                      value={newCristalForm.marca}
                      onChange={handleModalFormChange}
                      required
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                      placeholder="Nombre de la marca"
                    />
                    <button
                      type="button"
                      onClick={() => setModoNuevoCatalogoCampo((prev) => ({ ...prev, marca: false }))}
                      style={{ marginTop: '6px', border: 'none', background: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, fontSize: '12px' }}
                    >
                      Usar catálogo
                    </button>
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Producto:
                  </label>
                  {!modoNuevoCatalogoCampo.producto ? (
                    <select
                      name="producto"
                      value={newCristalForm.producto}
                      onChange={(e) => {
                        if (e.target.value === '__nuevo__') {
                          setModoNuevoCatalogoCampo((prev) => ({ ...prev, producto: true }));
                          setNewCristalForm((prev) => ({ ...prev, producto: '' }));
                          return;
                        }
                        handleModalFormChange(e);
                      }}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    >
                      <option value="">Seleccionar producto...</option>
                      {newCristalForm.producto && !valorEnCatalogo(opcionesCatalogo.productos, newCristalForm.producto) && (
                        <option value={newCristalForm.producto}>{`Valor actual: ${newCristalForm.producto}`}</option>
                      )}
                      {opcionesCatalogo.productos.map((item) => <option key={`producto-${item}`} value={item}>{item}</option>)}
                      <option value="__nuevo__">+ Crear nuevo producto...</option>
                    </select>
                  ) : (
                    <div>
                      <input
                        type="text"
                        name="producto"
                        value={newCristalForm.producto}
                        onChange={handleModalFormChange}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                        placeholder="Ej: Crizal, Transitions"
                      />
                      <button
                        type="button"
                        onClick={() => setModoNuevoCatalogoCampo((prev) => ({ ...prev, producto: false }))}
                        style={{ marginTop: '6px', border: 'none', background: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, fontSize: '12px' }}
                      >
                        Usar catálogo
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Material:
                  </label>
                  {!modoNuevoCatalogoCampo.material ? (
                    <select
                      name="material"
                      value={newCristalForm.material}
                      onChange={(e) => {
                        if (e.target.value === '__nuevo__') {
                          setModoNuevoCatalogoCampo((prev) => ({ ...prev, material: true }));
                          setNewCristalForm((prev) => ({ ...prev, material: '' }));
                          return;
                        }
                        handleModalFormChange(e);
                      }}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    >
                      <option value="">Seleccionar material...</option>
                      {newCristalForm.material && !valorEnCatalogo(opcionesCatalogo.materiales, newCristalForm.material) && (
                        <option value={newCristalForm.material}>{`Valor actual: ${newCristalForm.material}`}</option>
                      )}
                      {opcionesCatalogo.materiales.map((item) => <option key={`material-${item}`} value={item}>{item}</option>)}
                      <option value="__nuevo__">+ Crear nuevo material...</option>
                    </select>
                  ) : (
                    <div>
                      <input
                        type="text"
                        name="material"
                        value={newCristalForm.material}
                        onChange={handleModalFormChange}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                        placeholder="Ej: Orgánico, Policarbonato"
                      />
                      <button
                        type="button"
                        onClick={() => setModoNuevoCatalogoCampo((prev) => ({ ...prev, material: false }))}
                        style={{ marginTop: '6px', border: 'none', background: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, fontSize: '12px' }}
                      >
                        Usar catálogo
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                  Laboratorio:
                </label>
                {!modoNuevoCatalogoCampo.laboratorio ? (
                  <select
                    name="laboratorio"
                    value={newCristalForm.laboratorio}
                    onChange={(e) => {
                      if (e.target.value === '__nuevo__') {
                        setModoNuevoCatalogoCampo((prev) => ({ ...prev, laboratorio: true }));
                        setNewCristalForm((prev) => ({ ...prev, laboratorio: '' }));
                        return;
                      }
                      handleModalFormChange(e);
                    }}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                  >
                    <option value="">Seleccionar laboratorio...</option>
                    {newCristalForm.laboratorio && !valorEnCatalogo(opcionesCatalogo.laboratorios, newCristalForm.laboratorio) && (
                      <option value={newCristalForm.laboratorio}>{`Valor actual: ${newCristalForm.laboratorio}`}</option>
                    )}
                    {opcionesCatalogo.laboratorios.map((item) => <option key={`lab-${item}`} value={item}>{item}</option>)}
                    <option value="__nuevo__">+ Crear nuevo laboratorio...</option>
                  </select>
                ) : (
                  <div>
                    <input
                      type="text"
                      name="laboratorio"
                      value={newCristalForm.laboratorio}
                      onChange={handleModalFormChange}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                      placeholder="Ej: LOA, MOA"
                    />
                    <button
                      type="button"
                      onClick={() => setModoNuevoCatalogoCampo((prev) => ({ ...prev, laboratorio: false }))}
                      style={{ marginTop: '6px', border: 'none', background: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, fontSize: '12px' }}
                    >
                      Usar catálogo
                    </button>
                  </div>
                )}
              </div>

              {/* Fila 2: Índice y Precio */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Precio Público:
                  </label>
                  <input
                    type="number"
                    name="precio_publico"
                    value={newCristalForm.precio_publico}
                    onChange={handleModalFormChange}
                    step="0.01"
                    min="0"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px'
                    }}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Precio Efectivo:
                  </label>
                  <input
                    type="number"
                    name="precio_efectivo"
                    value={newCristalForm.precio_efectivo}
                    onChange={handleModalFormChange}
                    step="0.01"
                    min="0"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px'
                    }}
                    placeholder="Auto por descuento"
                  />
                </div>
              </div>

              {/* Fila 3: Tratamiento y opciones */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                    Tratamiento:
                  </label>
                  {!modoNuevoCatalogoCampo.tratamiento ? (
                    <select
                      name="tratamiento"
                      value={newCristalForm.tratamiento}
                      onChange={(e) => {
                        if (e.target.value === '__nuevo__') {
                          setModoNuevoCatalogoCampo((prev) => ({ ...prev, tratamiento: true }));
                          setNewCristalForm((prev) => ({ ...prev, tratamiento: '' }));
                          return;
                        }
                        handleModalFormChange(e);
                      }}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                    >
                      <option value="">Seleccionar tratamiento...</option>
                      {newCristalForm.tratamiento && !valorEnCatalogo(opcionesCatalogo.tratamientos, newCristalForm.tratamiento) && (
                        <option value={newCristalForm.tratamiento}>{`Valor actual: ${newCristalForm.tratamiento}`}</option>
                      )}
                      {opcionesCatalogo.tratamientos.map((item) => <option key={`trat-${item}`} value={item}>{item}</option>)}
                      <option value="__nuevo__">+ Crear nuevo tratamiento...</option>
                    </select>
                  ) : (
                    <div>
                      <input
                        type="text"
                        name="tratamiento"
                        value={newCristalForm.tratamiento}
                        onChange={handleModalFormChange}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                        placeholder="Ej: Antirreflejo, Blue Cut"
                      />
                      <button
                        type="button"
                        onClick={() => setModoNuevoCatalogoCampo((prev) => ({ ...prev, tratamiento: false }))}
                        style={{ marginTop: '6px', border: 'none', background: 'none', color: '#2563eb', cursor: 'pointer', padding: 0, fontSize: '12px' }}
                      >
                        Usar catálogo
                      </button>
                    </div>
                  )}
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="stock"
                    checked={newCristalForm.stock}
                    onChange={handleModalFormChange}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: '500' }}>Stock</span>
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="tiene_seguro"
                    checked={newCristalForm.tiene_seguro}
                    onChange={handleModalFormChange}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: '500' }}>Tiene Seguro</span>
                </label>

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="tiene_promocion"
                    checked={newCristalForm.tiene_promocion}
                    onChange={handleModalFormChange}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontWeight: '500' }}>Tiene Promoción</span>
                </label>
              </div>

              {/* Botones de acción */}
              <div style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'flex-end',
                paddingTop: '15px',
                borderTop: '1px solid #eee'
              }}>
                <button
                  type="button"
                  onClick={cerrarModal}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    guardarNuevoCristal();
                  }}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <CheckIcon style={{ width: '16px', height: '16px' }} />
                    Guardar Cristal
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CristalSearchInput;
